import { useState, useRef, useEffect, useMemo, useCallback, Fragment, type JSX } from 'react'
import {
  useTelegramChatMessages,
  chatDisplayName,
  isGroupChat,
  getChatTypeLabel,
  type TelegramMessage,
  type PendingTelegramMessage } from '../../hooks/useTelegramChats.js'
import { TicketGroupBlock } from '../chat-shared/TicketGroupBlock.js'
import { groupMessagesByTicket } from '../chat-shared/ticketGrouping.js'
import {
  Send,
  Ticket,
  X,
  UserPlus,
  Loader2,
  ChevronDown,
  ArrowRightLeft,
  MapPin,
  Paperclip,
  Download,
  ExternalLink,
  Users,
  Check,
  CheckCheck,
  AlertCircle,
  Eye,
  FileText,
  Bot,
} from 'lucide-react'
import { formatDateTime } from '../../utils/formatDate.js'
import { api } from '../../api/client.js'

interface Props {
  chatId: string | null
  onChanged?: () => void
  isAssignedToMe?: boolean
  isAiAssigned?: boolean
  aiAgentName?: string | null
  onOpenTransfer?: () => void
  onOpenDetails?: () => void
}

function TelegramIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  )
}

function LocationContent({ body }: { body: string }): JSX.Element {
  // Parse "Location: lat, lng" format
  const match = body.match(/Location:\s*([-\d.]+),\s*([-\d.]+)/)
  if (!match) return <p className="whitespace-pre-wrap break-words text-sm">{body}</p>
  
  const lat = match[1]
  const lng = match[2]
  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
  
  return (
    <div className="space-y-2">
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block overflow-hidden rounded-lg bg-gray-100"
      >
        <div className="flex h-32 items-center justify-center bg-gradient-to-br from-blue-100 to-green-100">
          <MapPin className="h-8 w-8 text-red-500" />
        </div>
      </a>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
      >
        <ExternalLink className="h-3 w-3" />
        Open in Google Maps
      </a>
      <p className="text-xs text-gray-500">{lat}, {lng}</p>
    </div>
  )
}

type MessageStatus = TelegramMessage['status'] | 'sending'

/** Message status ticks like WhatsApp */
function MessageTicks({ status }: { status: MessageStatus }): JSX.Element | null {
  switch (status) {
    case 'sending':
      return <Loader2 className="h-3 w-3 animate-spin text-blue-200" />
    case 'sent':
      return <Check className="h-3 w-3 text-blue-200" />
    case 'delivered':
      return <CheckCheck className="h-3 w-3 text-blue-200" />
    case 'read':
      return <CheckCheck className="h-3 w-3 text-white" />
    case 'failed':
      return <AlertCircle className="h-3 w-3 text-red-300" />
    default:
      // For 'received' or unknown statuses on inbound messages
      return null
  }
}

function MediaContent({ chatId, msg, isOutbound }: { chatId: string; msg: TelegramMessage; isOutbound: boolean }): JSX.Element | null {
  if (!msg.media_file_id) return null
  
  // Media is served through the API proxy
  const src = `/api/agent/telegram-chats/${chatId}/media/${msg.media_file_id}`
  const filename = msg.media_filename ?? 'file'
  
  // Action buttons for view and download
  const ActionButtons = ({ showView = true }: { showView?: boolean }): JSX.Element => (
    <div className="mt-2 flex items-center gap-2">
      {showView && (
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
            isOutbound
              ? 'bg-blue-400/30 text-white hover:bg-blue-400/50'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Eye className="h-3 w-3" />
          View
        </a>
      )}
      <a
        href={src}
        download={filename}
        className={`flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
          isOutbound
            ? 'bg-blue-400/30 text-white hover:bg-blue-400/50'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        <Download className="h-3 w-3" />
        Download
      </a>
    </div>
  )
  
  switch (msg.message_type) {
    case 'image':
    case 'sticker':
      return (
        <div className="mb-2">
          <img
            src={src}
            alt={msg.body ?? 'Image'}
            className="max-h-64 w-full rounded-lg object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
          <ActionButtons />
        </div>
      )
    case 'video':
      return (
        <div className="mb-2">
          <video src={src} controls className="max-h-64 w-full rounded-lg" />
          <ActionButtons />
        </div>
      )
    case 'audio':
    case 'voice':
      return (
        <div className="mb-2">
          <audio src={src} controls className="w-full" />
          <ActionButtons showView={false} />
        </div>
      )
    case 'document':
      return (
        <div className={`mb-2 rounded-lg p-3 ${isOutbound ? 'bg-blue-400/20' : 'bg-gray-50'}`}>
          <div className="flex items-center gap-2">
            <FileText className={`h-8 w-8 ${isOutbound ? 'text-white' : 'text-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <p className={`truncate text-sm font-medium ${isOutbound ? 'text-white' : 'text-gray-700'}`}>
                {filename}
              </p>
              {msg.media_mime_type && (
                <p className={`text-[10px] ${isOutbound ? 'text-blue-100' : 'text-gray-400'}`}>
                  {msg.media_mime_type}
                </p>
              )}
            </div>
          </div>
          <ActionButtons showView={false} />
        </div>
      )
    default:
      return null
  }
}

function MessageBubble({ chatId, msg, senderName }: { chatId: string; msg: TelegramMessage; senderName: string }): JSX.Element {
  const isOutbound = msg.direction === 'outbound'
  const isLocation = msg.message_type === 'location'
  const hasMedia = msg.media_file_id && ['image', 'video', 'audio', 'voice', 'document', 'sticker'].includes(msg.message_type)

  return (
    <div className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
      {/* Sender name */}
      <span className={`mb-0.5 px-1 text-[10px] font-medium ${isOutbound ? 'text-blue-600' : 'text-gray-600'}`}>
        {senderName}
      </span>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          isOutbound
            ? 'rounded-br-md bg-blue-500 text-white'
            : 'rounded-bl-md bg-white text-gray-900 shadow-sm'
        }`}
      >
        {/* Media content */}
        {hasMedia && <MediaContent chatId={chatId} msg={msg} isOutbound={isOutbound} />}
        
        {/* Location content */}
        {isLocation && msg.body && <LocationContent body={msg.body} />}
        
        {/* Text content (not for location which is handled above) */}
        {!isLocation && msg.body && (
          <p className="whitespace-pre-wrap break-words text-sm">{msg.body}</p>
        )}
        
        {/* Fallback for unknown types */}
        {!msg.body && !hasMedia && !isLocation && (
          <p className={`text-sm italic ${isOutbound ? 'text-blue-200' : 'text-gray-400'}`}>
            [{msg.message_type}]
          </p>
        )}
        
        {/* Timestamp and status ticks */}
        <div className={`mt-1 flex items-center justify-end gap-1 ${isOutbound ? 'text-blue-100' : 'text-gray-400'}`}>
          <span className="text-[10px]">
            {formatDateTime(msg.created_at)}
          </span>
          {isOutbound && <MessageTicks status={msg.status} />}
        </div>
        
        {/* Error message for failed sends */}
        {msg.status === 'failed' && msg.error_message && (
          <p className="mt-0.5 text-[10px] text-red-300">{msg.error_message}</p>
        )}
      </div>
    </div>
  )
}

function PendingBubble({
  msg,
  onDismiss,
}: {
  msg: PendingTelegramMessage
  onDismiss: (localId: string) => void
}): JSX.Element {
  return (
    <div className="flex justify-end">
      <div className="max-w-[75%] rounded-2xl rounded-br-md bg-blue-400 px-4 py-2 text-white opacity-70">
        <p className="whitespace-pre-wrap break-words text-sm">{msg.body}</p>
        <div className="mt-1 flex items-center justify-end gap-2">
          {msg.status === 'sending' && (
            <Loader2 className="h-3 w-3 animate-spin text-blue-100" />
          )}
          {msg.status === 'failed' && (
            <button
              onClick={() => onDismiss(msg.localId)}
              className="text-[10px] text-red-200 underline"
            >
              Failed — dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function TelegramChatThread({
  chatId,
  onChanged,
  isAssignedToMe,
  isAiAssigned,
  aiAgentName,
  onOpenTransfer,
  onOpenDetails,
}: Props): JSX.Element {
  const {
    chat,
    messages,
    conversations,
    pending,
    loading,
    loadingMore,
    hasMore,
    sendMessage,
    sendAttachment,
    saveContact,
    createTicket,
    markRead,
    dismissPending,
    loadMore,
  } = useTelegramChatMessages(chatId)

  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [creatingTicket, setCreatingTicket] = useState(false)
  const [ticketResult, setTicketResult] = useState<{ referenceId: string } | null>(null)
  const [showSaveContact, setShowSaveContact] = useState(false)
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)

  // Get sender/receiver names - for groups, use sender_name from message
  const isGroup = chat ? isGroupChat(chat) : false
  const chatName = chat ? chatDisplayName(chat) : 'Chat'
  const agentName = 'You' // TODO: Get actual agent name from context
  
  // Helper to get sender name for a message
  const getSenderName = useCallback((msg: TelegramMessage): string => {
    if (msg.direction === 'outbound') return agentName
    // For group chats, use the sender_name from the message
    if (isGroup && msg.sender_name) return msg.sender_name
    if (isGroup && msg.sender_username) return `@${msg.sender_username}`
    // For private chats, use the chat display name
    return chatName
  }, [isGroup, chatName, agentName])

  const unlinkedCount = useMemo(
    () => messages.filter((m) => !m.conversation_id).length,
    [messages],
  )

  // Group messages by conversation for proper display
  const groups = useMemo(() => groupMessagesByTicket(messages), [messages])
  const conversationById = useMemo(
    () => new Map(conversations.map((c) => [c.id, c])),
    [conversations],
  )

  useEffect(() => {
    setTicketResult(null)
    setSendError(null)
    setInput('')
    if (chatId) void markRead()
  }, [chatId, markRead])

  useEffect(() => {
    if (atBottom && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [messages, pending, atBottom])

  const handleScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50
    setAtBottom(isAtBottom)

    if (el.scrollTop < 100 && hasMore && !loadingMore) {
      void loadMore()
    }
  }, [hasMore, loadingMore, loadMore])

  async function handleSend(): Promise<void> {
    if (!isAssignedToMe || !chat || chat.status !== 'active') return
    if (sending || uploading) return
    if (!input.trim() && !file) return
    
    setSendError(null)
    
    // Handle file upload
    if (file) {
      setUploading(true)
      try {
        await sendAttachment(file, input.trim() || undefined)
        setFile(null)
        setInput('')
        onChanged?.()
      } catch {
        setSendError('File could not be sent.')
      } finally {
        setUploading(false)
      }
      return
    }
    
    // Handle text message
    setSending(true)
    try {
      await sendMessage(input)
      setInput('')
      onChanged?.()
    } catch {
      setSendError('Message could not be sent.')
    } finally {
      setSending(false)
    }
  }

  async function handleCreateTicket(): Promise<void> {
    if (!isAssignedToMe || creatingTicket || unlinkedCount === 0) return
    setCreatingTicket(true)
    setSendError(null)
    try {
      const result = await createTicket()
      if (result) {
        setTicketResult({ referenceId: result.referenceId })
        onChanged?.()
      }
    } catch {
      setSendError('Could not create the conversation.')
    } finally {
      setCreatingTicket(false)
    }
  }

  async function handleSaveContact(): Promise<void> {
    if (!contactName.trim()) return
    try {
      const emailValue = contactEmail.trim()
      await saveContact({ fullName: contactName.trim(), ...(emailValue ? { email: emailValue } : {}) })
      setShowSaveContact(false)
      setContactName('')
      setContactEmail('')
      onChanged?.()
    } catch {
      setSendError('Could not save contact.')
    }
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  if (!chatId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50">
        <TelegramIcon className="mb-3 h-12 w-12 text-blue-500/30" />
        <p className="text-sm text-gray-400">Select a chat to start messaging on Telegram</p>
      </div>
    )
  }

  const displayName = chat ? chatDisplayName(chat) : 'Telegram'

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden" style={{ background: 'linear-gradient(180deg, #d9e8f5 0%, #c8dae8 100%)' }}>
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-2.5">
        <div className={`flex h-9 w-9 items-center justify-center rounded-full ${isGroup ? 'bg-green-500/10' : 'bg-blue-500/10'}`}>
          {isGroup ? (
            <Users className="h-5 w-5 text-green-600" />
          ) : (
            <TelegramIcon className="h-5 w-5 text-blue-500" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-800">
            <button type="button" onClick={onOpenDetails} className="hover:text-blue-600">
              {displayName}
            </button>
          </h3>
          <p className="truncate text-[11px] text-gray-400">
            {isGroup && chat ? (
              <>
                {getChatTypeLabel(chat)}
                {chat.username ? ` · @${chat.username}` : ''}
              </>
            ) : (
              <>
                {chat?.username ? `@${chat.username}` : `ID: ${chat?.telegram_chat_id}`}
                {chat?.client_id ? ' · saved contact' : ''}
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Only show Add to contacts button for private chats, not groups */}
          {!isGroup && (
            <button
              onClick={() => {
                setShowSaveContact(true)
                setContactName(chat?.profile_name ?? chat?.first_name ?? '')
              }}
              title={chat?.client_id ? 'Update contact' : 'Add to contacts'}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <UserPlus className="h-4 w-4" />
            </button>
          )}
          {/* Only show Create Conversation button for private chats, not groups */}
          {!isGroup && (
            <button
              onClick={() => void handleCreateTicket()}
              disabled={!isAssignedToMe || unlinkedCount === 0 || creatingTicket}
              title={
                unlinkedCount === 0
                  ? 'Every message is already on a conversation'
                  : `Create a conversation from ${unlinkedCount} unlinked message${unlinkedCount === 1 ? '' : 's'}`
              }
              className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              <Ticket className="h-3.5 w-3.5" />
              {creatingTicket ? 'Creating…' : 'Create Conversation'}
              {unlinkedCount > 0 && !creatingTicket && (
                <span className="rounded-full bg-blue-500/15 px-1.5 text-[10px] font-semibold text-blue-600">
                  {unlinkedCount}
                </span>
              )}
            </button>
          )}
          {(isAssignedToMe || isAiAssigned) && onOpenTransfer && (
            <button
              onClick={onOpenTransfer}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100"
              title="Transfer"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {isAiAssigned && (
        <div className="flex items-center justify-between border-b border-purple-200 bg-purple-50 px-4 py-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-purple-700">
            <Bot className="h-3.5 w-3.5" />
            {aiAgentName ?? 'AI Agent'} is handling this chat
          </span>
          {!isAssignedToMe && (
            <button
              type="button"
              onClick={() => void api.post(`/agent/telegram-chats/${chatId}/assign`, {}).then(() => { onChanged?.() }).catch(() => {})}
              className="flex items-center gap-1.5 rounded-lg border border-purple-300 bg-white px-3 py-1 text-xs font-medium text-purple-700 transition-all hover:bg-purple-50 active:scale-[0.97]"
            >
              <UserPlus className="h-3 w-3" />
              Assign to Me
            </button>
          )}
        </div>
      )}

      {ticketResult && (
        <div className="flex items-center justify-between border-b border-green-200 bg-green-50 px-4 py-2">
          <span className="text-xs text-green-700">
            Conversation created: <strong>{ticketResult.referenceId}</strong>
          </span>
          <button onClick={() => setTicketResult(null)} className="text-gray-400 hover:text-gray-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="relative flex-1 overflow-hidden">
        {/* Telegram-style chat background pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="relative h-full overflow-y-auto thin-scrollbar px-4 py-4"
        >
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
              Loading older messages…
            </div>
          )}
          {loading && messages.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-500">
              Loading messages…
            </div>
          ) : messages.length === 0 && pending.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-500">
              No messages yet — say hello.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {groups.map((group) =>
                group.type === 'loose' ? (
                  <Fragment key={group.key}>
                    {group.messages.map((msg) => (
                      <MessageBubble
                        key={msg.id}
                        chatId={chatId}
                        msg={msg}
                        senderName={getSenderName(msg)}
                      />
                    ))}
                  </Fragment>
                ) : (
                  <TicketGroupBlock
                    key={group.key}
                    conversationId={group.conversationId}
                    conversation={conversationById.get(group.conversationId)}
                    messageCount={group.messages.length}
                    accentClassName="text-blue-500"
                  >
                    {group.messages.map((msg) => (
                      <MessageBubble
                        key={msg.id}
                        chatId={chatId}
                        msg={msg}
                        senderName={getSenderName(msg)}
                      />
                    ))}
                  </TicketGroupBlock>
                )
              )}
              {pending.map((p) => (
                <PendingBubble key={p.localId} msg={p} onDismiss={dismissPending} />
              ))}
            </div>
          )}
        </div>

        {!atBottom && (
          <button
            onClick={() => {
              if (containerRef.current) {
                containerRef.current.scrollTop = containerRef.current.scrollHeight
              }
              setAtBottom(true)
            }}
            className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-lg ring-1 ring-gray-200 transition-colors hover:bg-gray-50"
            title="Jump to latest"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        )}
      </div>

      {sendError && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-1.5 text-[11px] text-red-600">
          {sendError}
        </div>
      )}

      {/* Input */}
      {isAssignedToMe && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
          {/* File preview */}
          {file && (
            <div className="mb-2 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2">
              <Paperclip className="h-4 w-4 text-blue-500" />
              <span className="flex-1 truncate text-sm text-gray-700">{file.name}</span>
              <button
                onClick={() => setFile(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) setFile(f)
                e.target.value = ''
              }}
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!isAssignedToMe || !chat || chat.status !== 'active' || sending || uploading}
              title="Attach file"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-blue-500 disabled:opacity-40"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={file ? 'Add a caption…' : 'Type a message…'}
              disabled={!isAssignedToMe || !chat || chat.status !== 'active' || sending || uploading}
              className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none transition-colors focus:border-blue-400 focus:ring-1 focus:ring-blue-400/20 disabled:bg-gray-50 disabled:opacity-60"
            />
            <button
              onClick={() => void handleSend()}
              disabled={!isAssignedToMe || !chat || chat.status !== 'active' || (!input.trim() && !file) || sending || uploading}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600 disabled:opacity-40 disabled:hover:bg-blue-500"
            >
              {sending || uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      {/* Save Contact Dialog */}
      {showSaveContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              {chat?.client_id ? 'Update Contact' : 'Save Contact'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Full Name</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Email (optional)</label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowSaveContact(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleSaveContact()}
                disabled={!contactName.trim()}
                className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
